import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "utils/cn";
import {
  DialogCloseButton,
  DialogHeader,
  dialogOverlayClasses,
  type DialogHeaderProps,
} from "./Dialog";

// A Sheet is a Radix Dialog docked to the side of the screen, used for detail and edit panels.
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

const sheetContentVariants = cva(
  [
    "fixed inset-y-0 z-50 flex w-full flex-col bg-surface-raised text-foreground shadow-dialog outline-none ease-standard",
    "data-[state=open]:animate-in data-[state=open]:duration-base",
    "data-[state=closed]:animate-out data-[state=closed]:duration-fast",
  ],
  {
    variants: {
      side: {
        right:
          "right-0 border-l border-border data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
        left: "left-0 border-r border-border data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left",
      },
      size: {
        md: "max-w-md",
        lg: "max-w-[40rem]",
        xl: "max-w-[46rem]",
      },
    },
    defaultVariants: { side: "right", size: "md" },
  }
);

export interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof sheetContentVariants> {
  /** Leave out the built-in close button. */
  hideClose?: boolean;
}

export const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ className, side, size, hideClose = false, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className={dialogOverlayClasses} />
    <DialogPrimitive.Content
      ref={ref}
      data-ui="sheet"
      className={cn(sheetContentVariants({ side, size }), className)}
      {...props}
    >
      {children}
      {!hideClose && <DialogCloseButton className="absolute right-4 top-[1.125rem]" />}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
SheetContent.displayName = "SheetContent";

type DivProps = React.HTMLAttributes<HTMLDivElement>;

export type SheetHeaderProps = DialogHeaderProps;

export const SheetHeader: React.FC<SheetHeaderProps> = ({ className, ...props }) => (
  <DialogHeader
    data-ui="sheet-header"
    className={cn("shrink-0 border-b border-border py-5", className)}
    {...props}
  />
);

export const SheetBody = React.forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-5", className)}
      {...props}
    />
  )
);
SheetBody.displayName = "SheetBody";

export const SheetFooter: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border bg-surface-muted/50 px-6 py-4",
      className
    )}
    {...props}
  />
);
